import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { Truck, Car, Plane, Ship, Package, Warehouse, ArrowRight, Target, Globe, Shield, Network, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const LogistiqueMobilitePage = () => {
  const [heroRef, heroInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [mobiliteRef, mobiliteInView] = useInView();

  const logistiqueServices = [
    { icon: Package, title: 'Transport de marchandises', desc: 'Livraison et distribution adaptées aux territoires ultramarins.', gradient: 'from-orange-500 to-red-500' },
    { icon: Warehouse, title: 'Stockage & Entreposage', desc: 'Solutions de stockage sécurisées et logistique de proximité.', gradient: 'from-amber-500 to-orange-500' },
    { icon: Network, title: 'Gestion des flux', desc: 'Optimisation de votre chaîne logistique pour plus d\'efficacité.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: MapPin, title: 'Dernier kilomètre', desc: 'Livraison finale personnalisée et adaptée à vos contraintes.', gradient: 'from-green-500 to-emerald-500' },
  ];

  const mobiliteServices = [
    { icon: Car, title: 'O\'SCOP Mobilité', subtitle: 'Transport terrestre', desc: 'Solutions de transport collectif, navettes et covoiturage pour les déplacements quotidiens.', gradient: 'from-cyan-500 to-blue-500' },
    { icon: Plane, title: 'O\'SCOP Airways', subtitle: 'Mobilité aérienne', desc: 'Vols inter-îles, fret aérien et solutions charter adaptées aux Outre-mer.', gradient: 'from-sky-500 to-blue-500' },
    { icon: Ship, title: 'Transport maritime', subtitle: 'Fret maritime', desc: 'Solutions de transport maritime inter-territoires et international.', gradient: 'from-blue-500 to-indigo-500' },
  ];

  const advantages = [
    { icon: Globe, title: 'Couverture territoriale', desc: 'Présence dans tous les territoires ultramarins' },
    { icon: Clock, title: 'Réactivité', desc: 'Délais maîtrisés et flexibilité' },
    { icon: Shield, title: 'Fiabilité', desc: 'Sécurisation de vos marchandises' },
    { icon: Target, title: 'Sur-mesure', desc: 'Solutions adaptées à vos besoins' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1573209680076-bd7ec7007616?w=1920&q=80" 
            alt="Logistics and transportation" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-orange-600/20 to-red-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-orange-500/20 to-red-500/20 text-orange-300 border border-orange-500/30 mb-6 px-4 py-2">
              <Truck className="w-4 h-4 mr-2" /> Solutions logistiques
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-orange-100 to-white bg-clip-text text-transparent">Logistique &</span>
              <br />
              <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Mobilité</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Des solutions <span className="text-orange-400 font-semibold">adaptées aux territoires ultramarins</span> pour 
              optimiser vos flux logistiques et faciliter les déplacements.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
                Demander un devis <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Logistique Section */}
      <section ref={servicesRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 mb-4 px-4 py-2"><Warehouse className="w-4 h-4 mr-2" /> LOGI'SCOP</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Solutions <span className="text-orange-400">logistiques</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {logistiqueServices.map((service, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <service.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobilité Section */}
      <section ref={mobiliteRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-cyan-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${mobiliteInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 mb-4 px-4 py-2"><Car className="w-4 h-4 mr-2" /> Mobilité</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Solutions de <span className="text-cyan-400">mobilité</span></h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mobiliteServices.map((service, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl text-center ${mobiliteInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <service.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">{service.title}</h3>
                <p className="text-sm text-cyan-400 mb-4">{service.subtitle}</p>
                <p className="text-slate-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-24 bg-gradient-to-br from-orange-600 via-red-600 to-orange-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Nos <span className="text-orange-200">atouts</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((adv, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <adv.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{adv.title}</h3>
                <p className="text-sm text-white/70">{adv.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Besoin d'une solution <span className="text-orange-400">logistique</span> ?</h2>
          <p className="text-xl text-slate-400 mb-10">Contactez-nous pour étudier vos besoins et vous proposer une offre adaptée.</p>
          <Button size="lg" className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/contact'}>
            Nous contacter <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="LOGISCOP" />
      <Footer />
    </div>
  );
};

export default LogistiqueMobilitePage;
